import React, { useState } from "react";
import { useUserProgression } from "hooks/useUserProgression";
import cx from "classnames";
import { Skeleton } from "components/Skeleton";
import { SmallPromoIcon } from "components/PromoIcon/SmallPromoIcon";
import { defaultHelpLink } from "sections/Betting/CampaignPromotions";
import { Clamp } from "components/Clamp";
import type { BetEntry } from "sections/Entries/types";
import { Collapse } from "components/Collapse";
import { ReactComponent as TickIcon } from "components/assets/tick.svg";
import { Caret } from "components/Caret";

import * as styles from "./BetPickAutomation.module.scss";

type BetPickAutomationProps = {
  entry: BetEntry;
  progressionId: string;
};

const ProgressionStatusClamp = ({
  progressionStatus,
  isOpen,
}: {
  isOpen: boolean;
  progressionStatus: "PAID" | "NOT_PAID" | "IN_PROGRESS";
}) => (
  <Clamp
    variant="warning"
    className={cx(styles.statusClamp, {
      [styles.paid]: progressionStatus === "PAID",
      [styles.notPaid]: progressionStatus === "NOT_PAID",
      [styles.inProgress]: progressionStatus === "IN_PROGRESS",
    })}
  >
    {progressionStatus === "PAID" && <TickIcon />}
    {progressionStatus.replace("_", " ")}{" "}
    <Caret variant={isOpen ? "up" : "down"} />
  </Clamp>
);

const BetPickAutomation = ({
  progressionId,
  entry,
}: BetPickAutomationProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    campaign,
    isComplete,
    isFailed,
    loading,
    displayPayout,
    progression,
    hide,
  } = useUserProgression(progressionId, entry);

  const progressionStatus = isComplete
    ? isFailed
      ? "NOT_PAID"
      : "PAID"
    : "IN_PROGRESS";

  if (hide) return null;

  return (
    <div
      className={cx(styles.container, {
        [styles.hollow]: progressionStatus === "NOT_PAID",
      })}
      onClick={(e) => {
        e.preventDefault();
        setIsOpen((visible) => !visible);
      }}
    >
      {loading || !progression ? (
        <Skeleton />
      ) : (
        <>
          <SmallPromoIcon className={styles.promoIcon} />
          <div className={styles.content}>
            <div>
              <div className={styles.dullText}>{campaign?.name}</div>
              <div className={styles.payout}>{displayPayout}</div>
            </div>

            <ProgressionStatusClamp
              progressionStatus={progressionStatus}
              isOpen={isOpen}
            />
          </div>
          <Collapse isOpen={isOpen}>
            <div
              className={cx(styles.dashedLine, {
                [styles.dull]: progressionStatus === "NOT_PAID",
              })}
            />
            <div
              className={cx(styles.content)}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <div>{campaign?.description}</div>
              <a
                className={cx(styles.link, {
                  [styles.soft]: progressionStatus === "NOT_PAID",
                })}
                href={campaign?.termsOfUse ?? defaultHelpLink}
              >
                View Terms
              </a>
            </div>
          </Collapse>
        </>
      )}
    </div>
  );
};

export { BetPickAutomation };
